// Modal: editable JSON textarea for POSTing a one-off event to the
// source's webhook URL. No HMAC; intended for ad-hoc operator testing
// of trigger configs without involving the partner.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import { useState } from 'react';
import { Label } from '../common/atoms';

export function SendEventDialog({ webhookUrl, onClose }: { webhookUrl: string; onClose: () => void }) {
  const [body, setBody] = useState('{\n  "event": "test",\n  "amount": 42,\n  "user": "alice",\n  "ts": "' + new Date().toISOString() + '"\n}');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; status?: number; error?: string } | null>(null);

  async function handleSend() {
    setSending(true); setResult(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch (e) {
      setResult({ ok: false, error: `Invalid JSON: ${(e as Error).message}` });
      setSending(false);
      return;
    }
    try {
      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      setResult({ ok: res.ok, status: res.status });
    } catch (e) {
      setResult({ ok: false, error: String(e) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(560px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Send custom event</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>POST to <code style={{ fontFamily: 'var(--mono)' }}>{webhookUrl.split('/').slice(-2).join('/')}</code></div>

        <Label>JSON body</Label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={10}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 12,
            background: 'var(--bg-sunken)', color: 'var(--ink)',
            border: '1px solid var(--line)', borderRadius: 6, outline: 'none',
            resize: 'vertical',
          }}
        />

        {result && (
          <div style={{
            marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12,
            background: result.ok ? 'var(--accent-soft)' : 'var(--bg-sunken)',
            color: result.ok ? 'oklch(0.42 0.12 150)' : 'var(--danger)',
            border: `1px solid ${result.ok ? 'var(--accent-line)' : 'var(--line)'}`,
          }}>
            {result.ok
              ? `OK · accepted (${result.status}) — watch the Live tab for the event`
              : `Failed · ${result.error ?? `HTTP ${result.status}`}`}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose}>Close</button>
          <button className="btn primary" onClick={() => { void handleSend(); }} disabled={sending}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
